<template>
    <div class="admin-table-wrapper">
        <!-- Unified Action Header (optional) -->
        <div v-if="showBulkActions" class="admin-table-header">
            <!-- Unified Action Bar -->
            <div class="unified-action-bar">
                <!-- Selection Status - Only show when patients are selected -->
                <div class="selection-status">
                    <span v-if="selectedPatients && selectedPatients.length > 0" class="selected-count">
                        <i class="fa fa-check-circle text-success mr-1"></i>
                        <strong>{{selectedPatients.length}}</strong> patient{{selectedPatients.length > 1 ? 's' : ''}} selected
                    </span>
                </div>

                <!-- Action Buttons -->
                <div class="action-buttons">
                    <!-- List Management Actions (emit events for parent to handle) -->
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        class="action-btn"
                        @click="$emit('add-to-existing-list', selectedPatients)"
                        :disabled="!selectedPatients || selectedPatients.length === 0"
                    >
                        <i class="fa fa-plus mr-1"></i>
                        Add to Existing List
                    </b-button>
                    
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        class="action-btn"
                        @click="$emit('create-new-list', selectedPatients)"
                        :disabled="!selectedPatients || selectedPatients.length === 0"
                    >
                        <i class="fa fa-plus mr-1"></i>
                        Create New List
                    </b-button>

                    <!-- Clear Selection (only show when patients selected) -->
                    <b-button
                        v-if="selectedPatients && selectedPatients.length > 0"
                        variant="outline-secondary"
                        size="sm"
                        class="action-btn"
                        @click="clearSelection"
                    >
                        <i class="fa fa-times mr-1"></i>
                        Clear
                    </b-button>
                </div>
            </div>
        </div>

        <div class="table-responsive overdue-recall-table" :class="{ 'has-selection-col': hasSelectionColumn }">
            <v-client-table v-if="allSettings && data" :data="data" :columns="columns" :options="tableOptions" ref="tb" @row-click="onRowCLicked">

            <div slot="h__id">
                <b-form-group v-if="!isAdminPage()" class="abc-checkbox mb-1">
                    <input
                        type="checkbox"
                        id="selectAllCheckbox"
                        class="custom-control-input"
                        @click="isMainChecked ? chooseNone(selectedPatients) : chooseAll(data, selectedPatients)"
                        :checked="isMainChecked"
                    />
                    <label for="selectAllCheckbox"></label>
                </b-form-group>
            </div>
            <span slot="h__DateDue">Due Date</span>
            <span slot="h__dateBroken">Date</span>
            <span slot="h__DateSent">Date sent</span>
            <span slot="h__dateOfService">Date of Service</span>
            <span slot="h__type">Type</span>n>
            <!-- <span slot="h__attachedProcedures">Attached Procedures</span> -->
            <span slot="h__AdjustmentProcedureCode">Procedure Codes(s) / Adjustments(s)</span>
            <span slot="h__claimFee">Claim Fee / Ins Est</span>
            <!--<span slot="h__insuranceEstimate">Insurance Estimate</span>-->
            <span slot="h__fee">Fee</span>
            <span slot="h__RecallInfo">Recall Info</span>
            <span slot="h__dateTxPlanned">Date Tx Planned</span>
            <span slot="h__Provider">Provider</span>

            <!--
            <div id="insurance_" slot="h__insurance">Insurance</div>
            -->
           <!--
            <div slot="h__insAmts">Ins Amts (Primary) <br />
                <span>(Max/Used/Remaining)</span>
            </div>
            -->
            <span slot="h__insAmts" class="header-multiline">Ins Amts (Primary) <br /><small>(Max/Used)</small></span>
            <!--
            <span slot="h__txPlanAmtOffice">Tx Plan Amt <br /><small>(Office Fee)</small></span>
            <span slot="h__txPlanAmt">Tx Plan Amt <br /><small>(Adjusted)</small></span>
            -->
            <span slot="h__txPlanAmtInfo" class="header-multiline">Tx Plan Amt<br /><small>(FFS/Adjusted)</small></span>

            <span slot="h__guarantor">Guarantor</span>
            <span slot="h__amount">Amount</span>
            <span slot="h__lastVisit">Last Visit</span>
            <span slot="h__billingType">Billing Type</span>

            <span slot="h__FollowUpActionPlan">Follow up Action Plan</span>
            <span slot="h__Actions"></span>

            <!-- Custom filter slots to fix [object Object] placeholder issue -->
            <div slot="filter__AdjustmentProcedureCode">
                <input type="text" class="form-control" placeholder="Filter by Procedure Code" @input="$refs.tb.setFilter({ AdjustmentProcedureCode: $event.target.value })" />
            </div>
            <div slot="filter__fee">
                <input type="text" class="form-control" placeholder="Filter by Fee" @input="$refs.tb.setFilter({ fee: $event.target.value })" />
            </div>
            <div slot="filter__insurance">
                <input type="text" class="form-control" placeholder="Filter by Insurance" @input="$refs.tb.setFilter({ insurance: $event.target.value })" />
            </div>
            <div slot="filter__lastVisit">
                <DatePicker
                    v-model="lastVisitFilterDate"
                    lang="en"
                    :format="dateFormatStr"
                    placeholder="Filter by Last Visit"
                    input-class="form-control"
                    @input="onLastVisitFilterChange"
                    :clearable="true"
                    append-to-body>
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                </DatePicker>
            </div>

            <div slot="id" slot-scope="props">
                <b-form-group class="abc-checkbox mb-1">
                    <input
                        v-if="isAdminPage()"
                        type="checkbox"
                        class="custom-control-input"
                        :id="`checkbox${props.index}`"
                        v-model="props.row.completed"
                        disabled
                        />
                        <input
                        v-if="!isAdminPage()"
                        type="checkbox"
                        class="custom-control-input"
                        :id="`checkbox${props.index}`"
                        @click="updateSelectedPatients(selectedPatients, props.row)"
                        :checked="isSelectedPatient(selectedPatients, props.row.PatNum)"
                        />
                    <label :for="`checkbox${props.index}`"></label>
                </b-form-group>
            </div>
            <div slot="Patient" slot-scope="props">
                <small>
                    <span class="fw-semi-bold d-block">
                    <i class="fa fa-spinner fa-spin" style="font-size:20px; margin-right:5px" v-if="loadingStatus[Math.max(0, props.index-1)]"/>
                    <i class="fa fa-times text-danger" :id="`fu-error-${getUniqueID(props)}`" style="font-size:20px; margin-right:5px" v-show="loadingErrorStatus[Math.max(0, props.index-1)]"/>
                    <b-tooltip class="tool_tip" :target="`fu-error-${getUniqueID(props)}`" placement="righttop" triggers="hover">
                        Something went wrong while trying to make a follow-up attempt. Please try again in a moment.
                    </b-tooltip>

                    <!--
                    <router-link :to="{ name: extra.detailPageName, params: getParams(props.row.PatNum, props.row.ClaimNum, props.row.TeamioPatientID) }">{{formatName(props.row.Patient)}}</router-link>
                    -->
                    <!-- <a href="#">{{formatName(props.row.Patient)}}</a> -->
                    <a href="#" @click="createFollowup($event, props)">{{formatName(props.row.Patient)}}</a>
                    <span v-if="isOverdueRecallPage() || isOverdueRecallListPage()" class="text-muted d-inline-block">&nbsp;({{ props.row.Age }})</span>

                    <button
                      v-if="isSuperAdmin && (isOverdueRecallPage() || isOverdueRecallListPage())"
                      class="btn btn-sm ai-call-btn ml-2"
                      :class="{ 'ai-call-loading': aiCallLoading[props.row.PatNum] }"
                      :disabled="!canRowAICall(props.row) || aiCallLoading[props.row.PatNum]"
                      title="Trigger AI Call (simulation)"
                      @click.stop.prevent="onRowAICall(props.row)"
                    >
                      <i v-if="aiCallLoading[props.row.PatNum]" class="fa fa-spinner fa-spin"></i>
                      <span v-else>&#x1F916;</span>
                    </button>

                    <i v-show="props.row.BelongsToListID && props.row.BelongsToListID !== null" :id="`belongsto-${getUniqueID(props)}`" class="glyphicon glyphicon-list text-success mr-xs ml-2"></i>
                    <b-tooltip class="tool_tip" :target="`belongsto-${getUniqueID(props)}`" placement="righttop" triggers="hover">
                        <router-link :to="{ name: getListPageName(), params: getListParams(props.row.BelongsToListID)}">Belongs to list - {{props.row.ListName}}</router-link>
                    </b-tooltip>
                    </span>
                </small>

                <b-button v-if="(props.row.Status == 'Appointment Scheduled' ||  props.row.Status == 'Claim Received' ) && isAdminPage()" variant="success" class="mb-xs mr-xs btn-xs mt-xs">
                    <small><strong>{{ props.row.Status }}</strong></small>
                </b-button>

                <b-button v-if="isAdminPage() && !props.row.Status" variant="default" disabled class="mb-xs mr-xs btn-xs mt-xs">

                    <small><strong>Not Called</strong></small>
                </b-button>
                <b-button v-else-if="isAdminPage() && !(props.row.Status == 'Appointment Scheduled' || props.row.Status == 'Claim Received') " variant="default" disabled class="mb-xs mr-xs btn-xs mt-xs">

                    <small><strong>{{ props.row.Status }}</strong></small>
                </b-button>

                <p class="mt-xs" v-if="props.row.DateReceived && isAdminPage()">
                    <small>
                        <span class="d-inline-block">Date Received: <span class="text-muted">{{props.row.DateReceived | dateformat(dateFormatStr)}}</span></span>
                    </small>
                </p>

                <p v-if="props.row.DateSent && isAdminPage()">
                    <small>
                        <span>Date sent: <span class="text-muted">{{props.row.DateSent | dateformat(dateFormatStr)}}</span></span>
                    </small>
                </p>

                <p v-if="props.row.Carrier && isAdminPage()">
                    <small>
                        <span>Carrier: <span class="text-muted">{{props.row.Carrier}}</span></span>
                    </small>
                </p>

                <p v-if="(props.row.EmployeeManuallyAssigned === 1) && isAdminPage()">
                    <small>
                        <span>Assigned to: <span class="text-muted">{{getEmployeeName(props.row.AssignedToEmpID)}}</span></span>
                    </small>
                </p>

                <small v-if="props.row.Provider">
                    <span class="text-muted d-inline-block"><span class="fw-semi-bold">Provider:</span> {{props.row.Provider}}</span>
                    <p  v-if="props.row.hygienist !== null && isOverdueRecallPage()"><span class="text-muted d-inline-block"><span class="fw-semi-bold">Hygienist:</span> {{props.row.hygienist}}</span></p>
                </small>

                <p class="mt-xs" v-if="isAdminPageByEmployee() && props.row.PatientAmount">
                    <span class="d-inline-block">Balance: <span class="text-muted">{{props.row.PatientAmount | money(currencySymbol)}}</span></span>
                </p>
                <!--
                <span v-if="isAdminPage() && props.row.AttachedProcedures">Attached Procedures</span>
                <truncate
                    v-if="isAdminPage() && props.row.AttachedProcedures"
                    collapsed-text-class="collapsed"
                    clamp="More"
                    :length="25"
                    less="Show Less"
                    type="html"
                    :text="props.row.AttachedProcedures">
                </truncate>
                -->
            </div>

            <!--
                A bare "0" here is what sent us looking for a bug that wasn't one: the office saw
                0 mid-morning and reasonably concluded the reminder had failed, when the send was
                simply scheduled for later. The number alone can't distinguish "not yet" from
                "never" — so say which, and when.
            -->
            <div slot="AutoRemindersSent" slot-scope="props" v-if="(isOverdueRecallPage() || isOverdueRecallListPage()) && isAptReminderPaidFor">
                <p>
                    <small>
                        <span :id="`auto-rem-${getUniqueID(props)}`">
                            <span class="text-muted text-center">{{props.row.AutoRemindersSent}}</span>
                            <i v-if="!Number(props.row.AutoRemindersSent)"
                               class="fa fa-clock-o ml-1"
                               :class="recallReminderPending(props.row) ? 'text-muted' : 'text-warning'"></i>
                        </span>
                    </small>
                </p>
                <b-tooltip v-if="!Number(props.row.AutoRemindersSent)"
                           class="tool_tip"
                           :target="`auto-rem-${getUniqueID(props)}`"
                           placement="lefttop" triggers="hover">
                    {{ recallReminderTooltip(props.row) }}
                </b-tooltip>
            </div>

            <div slot="Guarantor" slot-scope="props">
                <small>
                    <span class="fw-semi-bold d-block">
                        <i class="fa fa-spinner fa-spin" style="font-size:20px; margin-right:5px" v-if="loadingStatus[Math.max(0, props.index-1)]"/>
                        <i class="fa fa-times text-danger" :id="`fu-guarantor-error-${getUniqueID(props)}`" style="font-size:20px; margin-right:5px" v-show="loadingErrorStatus[Math.max(0, props.index-1)]"/>
                        <b-tooltip class="tool_tip" :target="`fu-guarantor-error-${getUniqueID(props)}`" placement="righttop" triggers="hover">
                            Something went wrong while trying to make a follow-up attempt. Please try again in a moment.
                        </b-tooltip>
                        <!-- <router-link v-if="!isAdminPage()" :to="{ name: 'PatientBalancesDetails', params: { patientID: `${siteID}-${props.row.PatNum}` }}">{{formatName(props.row.Guarantor)}}</router-link> -->
                        <a href="#" @click="createFollowup($event, props)">{{formatName(props.row.Guarantor)}}</a>
                    </span>
                </small>
            </div>

            <div slot="dateBroken" slot-scope="props"><small>{{props.row.Date | dateformat(dateFormatStr)}}</small></div>

            <!--<div slot="DateSent" slot-scope="props"><small>{{props.row.DateSent | dateformat(dateFormatStr)}}</small></div>-->

            <div slot="DateSent" slot-scope="props" v-if="!isAdminPage()">
                <small>{{props.row.DateSent | dateformat(dateFormatStr)}}</small>
                 <p>
                <small v-if="props.row.ClaimType">
                    <span class="text-muted">Type:</span>
                    <span class="text-muted">&nbsp; {{props.row.ClaimType}}</span>
                </small>
                </p>
            </div>

            <div slot="dateReceived" slot-scope="props"><small>{{props.row.DateReceived | dateformat(dateFormatStr)}}</small></div>

            <div slot="DateDue" slot-scope="props"><small>{{props.row.DateDue | dateformat(dateFormatStr) }}</small></div>

            <div slot="dateOfService" slot-scope="props"><small>{{props.row.DateService | dateformat(dateFormatStr)}}</small></div>

            <!--<div slot="type" slot-scope="props"><small>{{props.row.ClaimType}}</small></div>-->

            <div slot="AdjustmentProcedureCode" slot-scope="props">
                {{props.row.AdjustmentProcedureCode }} <br>
                <span class="small" v-if="props.row.BrokenAppointmentProcDescript">
                    <small>
                        Description:
                        <truncate
                            v-if="props.row.BrokenAppointmentProcDescript"
                            collapsed-text-class="collapsed"
                            clamp="More"
                            :length="18"
                            less="Show Less"
                            type="html"
                            :text="props.row.BrokenAppointmentProcDescript">
                        </truncate>
                    </small>
                </span>
            </div>

            <div slot="fee" slot-scope="props"><small> {{props.row.Fee | money(currencySymbol)}}</small></div>

            <div slot="claimFee" slot-scope="props"><small>{{props.row.ClaimFee | money(currencySymbol)}}</small>

             <p>
             <small v-if="props.row.ClaimType">
                    <span class="text-muted">Ins Est:</span>
                    <span class="text-muted">&nbsp; {{props.row.InsuranceEstimate | money(currencySymbol)}}</span>
            </small>
            </p>

             </div>

            <!--<div slot="insuranceEstimate" slot-scope="props"><small>{{props.row.InsuranceEstimate | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</small></div>-->

            <!--

            <div slot="attachedProcedures" slot-scope="props">
                <small>
                <truncate
                    v-if="props.row.AttachedProcedures"
                    collapsed-text-class="collapsed"
                    clamp="More"
                    :length="18"
                    less="Show Less"
                    type="html"
                    :text="props.row.AttachedProcedures">
                </truncate>
                </small>
            </div>
            -->

            <div slot="Status" slot-scope="props">
                <!--
                     <b-badge :variant="statusColors[props.row.Status]">{{ props.row.Status }}</b-badge>
                 -->
                 <div v-if="(props.row.NextVisit !== null && (isUnscheduledTxPage() || isUnscheduledTxListPage()))">
                    <b-badge variant="success" class="text-white mt-1" pill >Apt on Schedule</b-badge>
                    <p class="fs-mini2 text-muted ml-2 mt-1"><i class="fa fa-calendar text-muted mr-xs mb-xs" />{{props.row.NextVisit | dateformat(dateFormatStr)}}</p>
                </div>
                 <b-badge variant="default" v-else>{{ props.row.Status }}</b-badge>
            </div>

            <div slot="dateTxPlanned" slot-scope="props"><small>{{props.row.dateTxPlanned | dateformat(dateFormatStr) }}</small></div>

            <div slot="insAmts" slot-scope="props">
                <small v-if="props.row.annualMax">{{props.row.annualMax | money(currencySymbol)}}/{{props.row.amountUsed | money(currencySymbol)}}</small>
                 <p>
                <small v-if="props.row.amountRemaining">
                    <span class="fw-semi-bold">Remaining:</span>
                    <span class="text-muted">&nbsp; {{props.row.amountRemaining | money(currencySymbol)}}</span>
                </small>
                </p>
            </div>

            <div slot="insurance" slot-scope="props">
                <!--{{props.row.Insurance}}-->
                <small>
                 <truncate
                    v-if="props.row.Insurance"
                    collapsed-text-class="collapsed"
                    clamp="More"
                    :length="15"
                    less="Show Less"
                    type="html"
                    :text="props.row.Insurance">
                </truncate>
                </small>
            </div>

            <!--
            <div slot="txPlanAmtOffice" slot-scope="props"><small>{{props.row.officeFeeTotal | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</small></div>

            <div slot="txPlanAmt" slot-scope="props"><small>{{props.row.adjustedFeeTotal | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</small></div>
            -->
            <div slot="txPlanAmtInfo" slot-scope="props"><small>{{props.row.officeFeeTotal | money(currencySymbol)}}</small>
             <p>
                <small>
                    <!--<span class="fw-semi-bold">Adjusted:</span>-->
                    <span class="text-muted">&nbsp; {{props.row.adjustedFeeTotal | money(currencySymbol)}}</span>
                </small>
                </p>
            </div>

            <div slot="RecallInfo" slot-scope="props">
                <p class="mb-0">
                  <small data-v-4d075910="">
                    <span class="fw-semi-bold">Type:</span>
                    <span class="text-muted">&nbsp; {{props.row.RecallType}}</span>
                  </small>
                </p>
                <p>
                <small>
                    <span class="fw-semi-bold">Interval:</span>
                    <span class="text-muted">&nbsp; {{props.row.RecallInterval}}</span>
                </small>
                </p>
            </div>
            <div slot="Carrier" slot-scope="props">
                <!--
                    {{props.row.Carrier}}
                -->

                <small>
                <truncate
                    v-if="props.row.Carrier"
                    collapsed-text-class="collapsed"
                    clamp="More"
                    :length="40"
                    less="Show Less"
                    type="html"
                    :text="props.row.Carrier">
                </truncate>
                </small>
                <p v-if="props.row.Carrier !== null">
                <small v-if="isOverdueRecallPage()">
                    <span class="fw-semi-bold">Freq at least 2 / year ? &nbsp;</span>
                    <span v-if="props.row.PreventiveFrequency2xPerYear === 'No'" class="text-muted">{{props.row.PreventiveFrequency2xPerYear}}</span>
                    <span v-else class="text-success fw-semi-bold">{{props.row.PreventiveFrequency2xPerYear}}</span>
                </small>
                </p>
            </div>

            <div slot="amount" slot-scope="props"><small>{{props.row.PatientAmount | money(currencySymbol)}}</small></div>

            <div slot="lastVisit" slot-scope="props"><small>{{props.row.LastVisit | dateformat(dateFormatStr)}}</small></div>

            <div slot="billingType" slot-scope="props"><small>{{props.row.BillingType}}</small></div>

            <div slot="LastHygienist" slot-scope="props"><small>{{props.row.LastHygienist}}</small></div>

            <div slot="FollowUpActionPlan" slot-scope="props" class="followup-action-cell">
                 <i class="fa fa-spinner fa-spin" style="font-size:20px;" v-if="followupActionPlanLoading[Math.max(0, props.index-1)]"/>

                <!--<b-badge variant="success" class="text-white" pill v-if="props.row.PendingUnscheduledTx === 1"></b-badge>-->

                <b-dropdown @shown="setSelectedEmployee(props)" id="change-followupdate-form" boundary="window" variant="white" size="sm" class="change-fu-date-dropdown" :ref="`${props.index-1}-change-followup-date-dropdown`" v-if="isDueToday(props.row.FollowupDate) && $can('update', 'followupDate')">
                      <template v-slot:button-content>
                        <b-badge variant="danger" class="text-white" pill >Due Today</b-badge>
                      </template>
                      <b-dropdown-form>
                        <b-form-group label="Change Follow-up Date to:" label-for="date-for-followup">
                            <DatePicker
                                v-model="followupDates[props.index-1]"
                                lang="en"
                                :format="dateFormatStr"
                                placeholder="Select a Date"
                                input-class="form-control"
                                :not-before="notBefore"
                                append-to-body>
                                <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                            </DatePicker>
                        </b-form-group>
                        <b-form-group v-if="(isAgingPage())" label="Assign to Employee:">
                            <multiselect
                            v-model="selectedEmployee[props.index-1]"
                            :options="filteredEmployees"
                            :multiple="false"
                            :taggable="false"
                            :show-labels="false"
                            :custom-label="assignedEmployeeCustomLabel"
                            label="Name"
                            track-by="EmployeeID"
                            :placeholder="showSelectedEmployee(props)"
                            aria-label="select employee"
                            @select="resetError"
                            >
                            <template slot="singleLabel" slot-scope="props">
                                <span class="custom__tag">
                                <span>{{ assignedEmployeeCustomLabel(props.option) }}</span>
                                <!-- <i tabindex="1" class="multiselect__tag-icon" @click="removeMember(props.option)"></i> -->
                                <!-- <span class="custom__remove" @click="removeMember(props.option)">X</span> -->
                                </span>
                            </template>
                            <template slot="option" slot-scope="props">
                                <!-- <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image" /> -->
                                <div class="option__desc">
                                    <span class="option__title">{{ assignedEmployeeCustomLabel(props.option) }}</span>
                                </div>
                            </template>
                            </multiselect>
                        </b-form-group>

                         <b-button :disabled="followupDateChangeInProgress[props.index-1]" variant="primary" size="sm" @click="changeFollowupDate(props)">Save</b-button>
                         <hr/>
                         <b-button variant="danger" size="sm" v-if="(props.row.Status === 'Not Called' || props.row.Status === null) && props.row.CreatedAutomatically === 1 && props.row.InitialFollowupDateSet === 1 "  @click="showSureModal('sure-modal-remove-initial-followup-date', props)">Remove Initial Set Follow-up</b-button>
                         <b-button variant="danger" size="sm" @click="showSureModal('sure-modal-stop-followups', props)" v-else>Stop Follow-ups</b-button>

                    </b-dropdown-form>
                </b-dropdown>

                <b-badge variant="danger" class="text-white" pill v-else-if="isDueToday(props.row.FollowupDate)">Due Today</b-badge>

                <b-badge variant="danger" class="text-white" pill v-else-if="props.row.TeamioFollowupStatusID === 3 || props.row.FollowupStopped === 1">Do Not Follow-up</b-badge>
                <b-badge variant="warning" class="text-white" pill v-else-if="props.row.FollowupDate === null && props.row.Attempts > 0 && props.row.FollowupStopped === 0">Follow-ups done</b-badge>


                <b-dropdown @shown="setSelectedEmployee(props)" id="dropdown-form" boundary="window" text="Not Set" variant="gray" :ref="`${props.index-1}-dropdown`" size="sm" class="not-set-dropdown" v-else-if="props.row.FollowupDate === null && props.row.Status === 'Not Called' ">
                    <b-dropdown-form>
                        <b-form-group label="Start Following Up On:" label-for="date-for-followup">
                            <DatePicker
                                v-model="followupDates[props.index-1]"
                                lang="en"
                                :format="dateFormatStr"
                                placeholder="Select a Date"
                                input-class="form-control"
                                :not-before="notBefore"
                                append-to-body>
                                <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                            </DatePicker>
                        </b-form-group>

                        <b-form-group v-if="(isAgingPage())" label="Assign to Employee:">
                            <multiselect
                            v-model="selectedEmployee[props.index-1]"
                            :options="filteredEmployees"
                            :multiple="false"
                            :taggable="false"
                            :show-labels="false"
                            :custom-label="assignedEmployeeCustomLabel"
                            label="Name"
                            track-by="EmployeeID"
                            :placeholder="showSelectedEmployee(props)"
                            aria-label="select employee"
                            @select="resetError"
                            >
                            <template slot="singleLabel" slot-scope="props">
                                <span class="custom__tag">
                                <span>{{ assignedEmployeeCustomLabel(props.option) }}</span>
                                <!-- <i tabindex="1" class="multiselect__tag-icon" @click="removeMember(props.option)"></i> -->
                                <!-- <span class="custom__remove" @click="removeMember(props.option)">X</span> -->
                                </span>
                            </template>
                            <template slot="option" slot-scope="props">
                                <!-- <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image" /> -->
                                <div class="option__desc">
                                    <span class="option__title">{{ assignedEmployeeCustomLabel(props.option) }}</span>
                                </div>
                            </template>
                            </multiselect>
                        </b-form-group>

                        <b-form-group>
                            <textarea v-model="initialFollowupNotes[props.index-1]" rows="3" max-rows="3" cols="10" name :placeholder="followupNotePlaceholder" class="form-control form-control-sm"></textarea>
                        </b-form-group>
                         <b-button :disabled="initialFollowupInProgress[props.index-1]" variant="primary" size="sm" @click="saveInitialFollowupDate(props)">Save</b-button>
                         <!-- <p class="pt-2" v-if="followupDates[props.index]"><a @click="followupDates[props.index]=null">Clear date</a></p>  -->
                    </b-dropdown-form>
                </b-dropdown>

                <b-button variant="default followup" disabled v-else-if="(props.row.Status != 'Appointment Scheduled' && props.row.Status != 'Claim Received' ) && !$can('update', 'followupDate')">
                    <i class="fa fa-calendar text-success mr-xs mb-xs" />
                    <small><strong>{{props.row.FollowupDate | dateformat(dateFormatStr)}}</strong></small>
                </b-button>

                <b-dropdown @shown="setSelectedEmployee(props)" id="change-followupdate-form" boundary="window" variant="default" size="sm" class="change-fu-date-dropdown" :ref="`${props.index-1}-change-followup-date-dropdown`" v-else-if="(props.row.Status != 'Appointment Scheduled' && props.row.Status != 'Claim Received') && $can('update', 'followupDate')">
                      <template v-slot:button-content>
                        <i class="fa fa-calendar text-success mr-xs mb-xs" />
                        <span class="fw-semi-bold">{{props.row.FollowupDate | dateformat(dateFormatStr)}}</span>
                      </template>
                      <b-dropdown-form>
                        <b-form-group label="Change Follow-up Date to:" label-for="date-for-followup">
                            <DatePicker
                                v-model="followupDates[props.index-1]"
                                lang="en"
                                :format="dateFormatStr"
                                placeholder="Select a Date"
                                input-class="form-control"
                                :not-before="notBefore"
                                append-to-body>
                                <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                            </DatePicker>
                        </b-form-group>
                        <b-form-group v-if="(isAgingPage())" label="Assign to Employee:">
                            <multiselect
                            v-model="selectedEmployee[props.index-1]"
                            :options="filteredEmployees"
                            :multiple="false"
                            :taggable="false"
                            :show-labels="false"
                            :custom-label="assignedEmployeeCustomLabel"
                            label="Name"
                            track-by="EmployeeID"
                            :placeholder="showSelectedEmployee(props)"
                            aria-label="select employee"
                            @select="resetError"
                            >
                            <template slot="singleLabel" slot-scope="props">
                                <span class="custom__tag">
                                <span>{{ assignedEmployeeCustomLabel(props.option) }}</span>
                                <!-- <i tabindex="1" class="multiselect__tag-icon" @click="removeMember(props.option)"></i> -->
                                <!-- <span class="custom__remove" @click="removeMember(props.option)">X</span> -->
                                </span>
                            </template>
                            <template slot="option" slot-scope="props">
                                <!-- <img class="option__image" src='../../assets/icons-user-40.png' alt="Team member image" /> -->
                                <div class="option__desc">
                                    <span class="option__title">{{ assignedEmployeeCustomLabel(props.option) }}</span>
                                </div>
                            </template>
                            </multiselect>
                        </b-form-group>
                         <b-button :disabled="followupDateChangeInProgress[props.index-1]" variant="primary" size="sm" @click="changeFollowupDate(props)">Save</b-button>
                         <hr/>
                         <b-button variant="danger" size="sm" v-if="(props.row.Status === 'Not Called' || props.row.Status === null) && props.row.CreatedAutomatically === 1 && props.row.InitialFollowupDateSet === 1" @click="showSureModal('sure-modal-remove-initial-followup-date', props)">Remove Initial Set Follow-up</b-button>
                         <b-button variant="danger" size="sm" @click="showSureModal('sure-modal-stop-followups', props)" v-else>Stop Follow-ups</b-button>
                    </b-dropdown-form>
                </b-dropdown>
                <!--
                    The below condition for resetting follow up is same as if "Do Not Follow up" is to show OR if "Follow-ups done" is to show
                -->
                <span class="followup-icons-inline">
                    <i v-show="(props.row.TeamioFollowupStatusID === 3 || props.row.FollowupStopped === 1) || (props.row.FollowupDate === null && props.row.Attempts > 0 && props.row.FollowupStopped === 0)" :id="`followup-reset-${getUniqueID(props)}`" @click="showSureModal('sure-modal-reset-followups', props)" class="la la-refresh  fw-bold mr-xs ml-2"></i>
                    <b-tooltip class="tool_tip" :target="`followup-reset-${getUniqueID(props)}`" placement="left" triggers="hover">
                        Click to start following up again and Restart the Follow-up Cycle!
                    </b-tooltip>

                    <i v-show="props.row.pastDue === 'true'" :id="`followup-action-plan-${getUniqueID(props)}`" class="fa fa-circle text-danger mr-xs ml-2"></i>
                    <b-tooltip class="tool_tip" :target="`followup-action-plan-${getUniqueID(props)}`" placement="righttop" triggers="hover">
                        Past Due!
                    </b-tooltip>

                    <i v-show="props.row.Overridden === 1" :id="`overridden-${getUniqueID(props)}`" class="fa fa-exclamation text-danger mr-xs ml-2"></i>
                    <b-tooltip class="tool_tip" :target="`overridden-${getUniqueID(props)}`" placement="righttop" triggers="hover">
                        This patient's Follow-up date was Overriden in the past.
                    </b-tooltip>

                    <i v-show="props.row.InitialFollowupNote" :id="`initialfunote-${getUniqueID(props)}`" class="glyphicon glyphicon-list-alt text-grey mr-xs ml-2"></i>
                    <b-tooltip class="tool_tip" :target="`initialfunote-${getUniqueID(props)}`" placement="righttop" triggers="hover">
                        {{props.row.InitialFollowupNote}}
                    </b-tooltip>
                </span>
                <div v-if="(isAgingPage() && props.row.EmployeeManuallyAssigned === 1)">
                    <small>
                        <span class="text-muted d-inline-block"><span class="fw-semi-bold">Assigned to:</span> {{getEmployeeName(props.row.AssignedToEmpID)}}</span>
                    </small>
                </div>
            </div>
            <div slot="Attempts" slot-scope="props">
                <div v-if="props.row.Attempts" :id="`attempt-${getUniqueID(props)}`" class="attempts-icons">
                    <!-- Show first 3 attempt icons -->
                    <span v-for="(attempt, counter) in props.row.AttemptDetails.split(',').slice(0, 3)" :key="counter">
                        <i v-if="attempt === 'P'" class="fa fa-phone text-success mr-1"></i>
                        <i v-else-if="attempt === 'E'" class="fa fa-envelope text-success mr-1"></i>
                        <i v-else class="fa fa-mobile text-success mr-1"></i>
                    </span>
                    <!-- Show +N indicator if more than 3 attempts -->
                    <span v-if="props.row.AttemptDetails.split(',').length > 3" class="attempts-more-indicator">
                        +{{ props.row.AttemptDetails.split(',').length - 3 }}
                    </span>
                    <b-tooltip class="tool_tip" :target="`attempt-${getUniqueID(props)}`" placement="left" triggers="hover">
                        {{ props.row.Attempts }} attempt(s) made so far
                    </b-tooltip>
                </div>
                <span v-else class="glyphicon glyphicon-ban-circle text-danger" :id="`no-attempt-${getUniqueID(props)}`">
                    <b-tooltip class="tool_tip" :target="`no-attempt-${getUniqueID(props)}`" placement="left" triggers="hover">
                        No attempts made for this patient yet
                    </b-tooltip>
                </span>
            </div>
            <div slot="Actions" slot-scope="props">
                <span class="removeFromList fa fa-trash-o text-muted" :id="`remove-from-list-${getUniqueID(props)}`" @click="_removePatient(props.row)"/>
                <b-tooltip class="tool_tip" :target="`remove-from-list-${getUniqueID(props)}`" placement="left" triggers="hover">
                    Remove from list
                </b-tooltip>
            </div>

            <!--
            <div slot="taskContent" slot-scope="props">
                <todo-item
                    :item="props.row"
                    :moreThanOneItems="props.row.length > 1"
                    :isLoading="false"
                />
            </div>
            -->

        </v-client-table>
        <slideout-panel></slideout-panel>
        <b-modal ref="sure-modal-remove-initial-followup-date" size="sm" body-class="bg-white" title="Are you sure?" @ok="runSureOk('REMOVE-INITIAL-FOLLOWUP-DATE')" @cancel="hideSureModal('sure-modal-remove-initial-followup-date')">
            Are you sure you want to delete this Initial Follow-up?
        </b-modal>
        <b-modal ref="sure-modal-stop-followups" size="sm" body-class="bg-white" title="Are you sure?" @ok="runSureOk('STOP-FOLLOWUPS')" @cancel="hideSureModal('sure-modal-stop-followups')">
            Are you sure you want to stop future Follow-ups?
        </b-modal>
        <b-modal ref="sure-modal-reset-followups" size="sm" body-class="bg-white" title="Are you sure?" @ok="runSureOk('RESET-FOLLOWUPS')" @cancel="hideSureModal('sure-modal-reset-followups')">
            Are you sure you want to reset the Follow-up Cycle and start following up again?
        </b-modal>

        </div>
    </div>
</template>



<script>

/* eslint-disable */

import Vue from 'vue';
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import mixin from '../../mixin.js';
import truncate from 'vue-truncate-collapsed';
import moment from 'moment-timezone';
import { mapActions } from 'vuex';
import CreateFollowup from './components/CreateFollowup';
import _ from 'lodash';
import ToastComponent from './components/ToastComponent';
import { triggerAutorecallCall } from '@/api/autorecall';

export default {
  name: 'AdminTable',
  components: { DatePicker, truncate, Multiselect },
  // showBulkActions gates the whole selection action bar (Add to Existing List /
  // Create New List / Clear / the "N selected" status), NOT just one button — it was
  // called showMassMessaging until the Send Mass Message feature was retired in favour
  // of Campaigns (2026-07-29).
  props: ['data', 'columns', 'options', 'extra', 'selectedPatients', 'showBulkActions', 'tableTitle'],
  mixins: [mixin],

  created() {
    // Register lifecycle cleanup for follow-up locks
    window.addEventListener('beforeunload', this._cleanupFollowupSessions);
    document.addEventListener('visibilitychange', this._handleVisibilityChange);
  },

  beforeDestroy() {
    // Best-effort cleanup when component is torn down
    try {
      this._cleanupFollowupSessions();
    } catch (e) {
      console.error('[FOLLOWUP] Cleanup error on destroy:', e);
    }
    window.removeEventListener('beforeunload', this._cleanupFollowupSessions);
    document.removeEventListener('visibilitychange', this._handleVisibilityChange);
  },


  data() {
      return {
          selectedRowId: null,
          notBefore: new Date(),
          followupActionPlanLoading: {},
          initialFollowupNotes: [],
          followupDates: [],
          selectedEmployee: [],
          results: [],
          attemptTracking: null,
          teamioPatientID: null,
          lastFollowedUpWithTeamioPatientID: null,
          loadingStatus: [].repeat(false, 50),
          loadingErrorStatus: [].repeat(false, 50),
          initialFollowupInProgress: [].repeat(false, 50),
          followupDateChangeInProgress: [].repeat(false, 50),
          followupParams: null,
          lastVisitFilterDate: null,
          aiCallLoading: {},
      }
  },

  methods: {
    onLastVisitFilterChange(date) {
      if (date) {
        // Format date for filtering - use the same format as displayed in the table
        const formattedDate = this.$options.filters.dateformat(date, this.dateFormatStr);
        this.$refs.tb.setFilter({ lastVisit: formattedDate });
      } else {
        // Clear the filter when date is cleared
        this.$refs.tb.setFilter({ lastVisit: '' });
      }
    },

    async _cleanupFollowupSessions() {
      // Clears any lingering locks for active sessions
      try {
        await this.$store.dispatch('cache/emergencyCleanupAllSessions');
      } catch (err) {
        console.error('[FOLLOWUP] Emergency cleanup failed:', err);
      }
    },

    _handleVisibilityChange() {
      if (document.hidden) {
        this._cleanupFollowupSessions();
      }
    },

    canRowAICall(row) {
      return !!(row.WirelessPhone && row.TxtMsgOk === 1);
    },

    async onRowAICall(row) {
      if (!this.canRowAICall(row)) return;
      Vue.set(this.aiCallLoading, row.PatNum, true);
      try {
        const result = await triggerAutorecallCall({
          siteID: this.siteID,
          patNum: row.PatNum,
          taskID: row.TaskID || null,
          callReason: 'recall'
        });
        if (result.success) {
          this.$toast.success(`AI call: ${result.outcome || 'completed'} (${result.mode || 'simulation'})`);
        } else {
          this.$toast.warning(result.message || result.reason || 'AI call did not succeed');
        }
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.message) || err.message;
        this.$toast.error('AI call failed: ' + msg);
      } finally {
        Vue.set(this.aiCallLoading, row.PatNum, false);
      }
    },

    ...mapActions('tasks', ['updateInitialFollowupDate', 'updateFollowupDate', 'removeInitialFollowupDate', 'stopFollowups', 'resetFollowups']),
    ...mapActions('teamio', ['runQuery', 'startTrackingFUAttempt', 'stopTrackingFUAttempt']),

    showSureModal(modal, props) {
        this.followupParams = props;
        this.$refs[modal].show();
    },

    hideSureModal(ref) {
      this.$refs[ref].hide();
    },

    hideModal(ref) {
      this.$refs[ref].hide()
    },

    assignedEmployeeCustomLabel({Name}){
        return `${Name}`;
    },

    showSelectedEmployee(props){
       if (props.row.EmployeeManuallyAssigned === 1){
        return this.getEmployeeName(props.row.AssignedToEmpID);
       }
       else return 'Select employee (optional)';
    },

    setSelectedEmployee(props){
        let i = Math.max(0, props.index - 1);
        if (props.row.EmployeeManuallyAssigned === 1){
            //get object from AssignedToEmpID
            this.selectedEmployee[i] = _.find(this.filteredEmployees,employee =>
                employee.EmployeeID === props.row.AssignedToEmpID
            );
            console.log(this.selectedEmployee[i]);
       }  
    },

    onRowCLicked(data) {
        const clickedRowId = this.getUniqueID(data);

        if (this.selectedRowId === clickedRowId) {
            this.selectedRowId = null;
        } else {
            this.selectedRowId = clickedRowId;
        }

        /* Remove direct mutation of the prop data
        if(originalDataIndex !== -1) {
            if(data.row.teamioActive) {
                Vue.set(this.data[originalDataIndex], 'teamioActive', !data.row.teamioActive);
            }
            else {
                Vue.set(this.data[originalDataIndex], 'teamioActive', true);
            }
        }
        */
    },

    customRowClassCallback(row) {
        const rowId = this.getUniqueID({ row: row });
        //console.log(`AdminTable Callback - Row ID: ${rowId}, Selected ID: ${this.selectedRowId}, Match: ${rowId === this.selectedRowId}`); // Log callback details
        return rowId === this.selectedRowId ? 'selectedRow' : '';
    },

    async changeFollowupDate(props) {
        //console.log(`changeFollowupDate called with ${JSON.stringify(props)}`);
        let i = Math.max(0, props.index - 1);
        if(this.followupDates[i]) {
            this.followupDateChangeInProgress[i] = true;
            Vue.set(this.followupActionPlanLoading, i, true);

            let payload = {
                SiteID: this.siteID,
                EmployeeID: this.userID,
                TaskTypeID: this.getTaskTypeID(),
                AssignedToEmpID: this.selectedEmployee[i] ? this.selectedEmployee[i].EmployeeID : this.getAssignedToEmployeeID,
                TeamioPatientID: this.getTeamioPatientID(props),
                FollowupDate: moment(this.followupDates[i]).format('YYYY-MM-DD'),
                OldFollowupDate: moment(props.row.FollowupDate).format('MM-DD-YYYY'),
                EmployeeManuallyAssigned: (typeof this.selectedEmployee[i] === 'object' && this.selectedEmployee[i] !== null) ? 1 : 0,
                ClaimNum: props.row.ClaimNum,
            }

            //console.log(`changeFollowupDate payload: ${JSON.stringify(payload)}`);

            let originalDataIndex = this.getOriginalDataIndex(props);
            //console.log(`originalDataIndex: ${originalDataIndex}`);
            try {
                await this.updateFollowupDate(payload);
                //console.log(`original index: ${originalDataIndex} and data: ${JSON.stringify(this.data[originalDataIndex])}`);
                if(originalDataIndex !== -1)
                    Vue.set(this.data[originalDataIndex], "FollowupDate", moment(this.followupDates[i]).format('YYYY-MM-DD'));
                Vue.set(this.data[originalDataIndex], 'pastDue', 'false');
                if (typeof this.selectedEmployee[i] === 'object' && this.selectedEmployee[i] !== null){
                        Vue.set(this.data[originalDataIndex],"EmployeeManuallyAssigned",1);
                        //this is so it displays the assigned employees name right away
                        Vue.set(this.data[originalDataIndex],"AssignedToEmpID",this.selectedEmployee[i].EmployeeID);
                }
                if(moment(this.followupDates[i]).isSame(moment(), 'day')) {
                    Vue.set(this.data[originalDataIndex], 'dueToday', 'true');
                }
            } catch (err) {
                console.error(err);
            } finally {
                Vue.set(this.followupActionPlanLoading, i, null);
                this.followupDateChangeInProgress[i] = false;
                this.$refs[`${props.index-1}-change-followup-date-dropdown`].hide(true);
            }
        }
    },

    async saveInitialFollowupDate(props) {
        let i = Math.max(0, props.index - 1);

        if(this.followupDates[i]) {
            this.initialFollowupInProgress[i] = true;
            //LogRocket.log(`in saveInitialFollowupDate, pros: ${JSON.stringify(props)} and row index: ${i} and followupDates[i] = ${this.followupDates[i]}`);
            //console.log(`in saveInitialFollowupDate, pros: ${JSON.stringify(props)} and row index: ${i} and followupDates[i] = ${this.followupDates[i]}`);
            Vue.set(this.followupActionPlanLoading, i, true);

            let belongsToListID = this.getBelongsToListID(props);
            //console.log(`belongsToListID: ${belongsToListID}`);
            //console.log(`props: ${JSON.stringify(props)}`);
            //LogRocket.log(`belongsToListID: ${belongsToListID}`);

            let payload = {
                CreatedAt: moment().format('YYYY-MM-DD'),
                SiteID: this.siteID,
                CreatedAutomatically: 1,
                AssignedToEmpID: this.selectedEmployee[i] ? this.selectedEmployee[i].EmployeeID : this.getAssignedToEmployeeID,
                DueDate: moment(this.followupDates[i]).format('YYYY-MM-DD'),
                StatusID: 1,
                TaskTypeID: this.getTaskTypeID(),
                PatNum: props.row.PatNum,
                UserODNum: this.user.ODUserNum,
                DefaultDB: this.user.DefaultDB,
                TeamioPatientID: `${this.siteID}-${props.row.PatNum}`,
                FollowupDate: moment(this.followupDates[i]).format('YYYY-MM-DD'),
                Active: 1,
                Attempts: 0,
                BelongsToListID: belongsToListID,
                ClaimNum: props.row.ClaimNum ? props.row.ClaimNum : null,
                InitialFollowupDateSet: 1, // this is how we are tracking that this task is created automatically BUT also is for setting initial follow up
                InitialFollowupCreatedOn: moment().format('YYYY-MM-DD'),
                EmployeeManuallyAssigned: (typeof this.selectedEmployee[i] === 'object' && this.selectedEmployee[i] !== null) ? 1 : 0,
                ...(this.initialFollowupNotes[i] && { initialFollowupNote: this.initialFollowupNotes[i] }),
            }
            //LogRocket.log(`saveInitialFollowupDate payload: ${JSON.stringify(payload)}`);
            //console.log(`saveInitialFollowupDate payload: ${JSON.stringify(payload)}`);

            try {
                await this.updateInitialFollowupDate(payload);
                let originalDataIndex = this.getOriginalDataIndex(props);
                //console.log(`original index: ${originalDataIndex} and data: ${JSON.stringify(this.data[originalDataIndex])}`);
                if(originalDataIndex !== -1) {
                    Vue.set(this.data[originalDataIndex], "FollowupDate", moment(this.followupDates[i]).format('YYYY-MM-DD'));
                    Vue.set(this.data[originalDataIndex],"InitialFollowupDateSet",1);
                    Vue.set(this.data[originalDataIndex],"CreatedAutomatically",1);
                    if (typeof this.selectedEmployee[i] === 'object' && this.selectedEmployee[i] !== null){
                        Vue.set(this.data[originalDataIndex],"EmployeeManuallyAssigned",1);
                        //this is so it displays the assigned employees name right away
                        Vue.set(this.data[originalDataIndex],"AssignedToEmpID",this.selectedEmployee[i].EmployeeID);
                    }
                    if(this.initialFollowupNotes[i] !== '')
                        Vue.set(this.data[originalDataIndex], "InitialFollowupNote", this.initialFollowupNotes[i]);
                }
                Vue.set(this.followupActionPlanLoading, i, null);
            } catch (err) {
                Vue.set(this.followupActionPlanLoading, i, null);
                console.error(err);
            } finally {
                this.$refs[`${props.index-1}-dropdown`].hide(true);
                this.initialFollowupInProgress[i] = false;
            }
        }
    },

    runSureOk(confirmFor) {
        switch(confirmFor) {
            case 'REMOVE-INITIAL-FOLLOWUP-DATE':
                                                    //console.log(`now removing initial followup date`);
                                                    this._removeInitialFollowupDate();
                                                    break;
            case 'STOP-FOLLOWUPS':                  //console.log(`now stopping future followups`);
                                                    this._stopFollowups();
                                                    break;

            case 'RESET-FOLLOWUPS':                  //console.log(`now resetting followups`);
                                                    this._resetFollowups();
                                                    break;
            default: break;
        }
    },

    async _removeInitialFollowupDate() {
        let props = this.followupParams;
        let i = Math.max(0, props.index - 1);
        this.followupDateChangeInProgress[i] = true;
        Vue.set(this.followupActionPlanLoading, i, true);

        let payload = {
            SiteID: this.siteID,
            TeamioPatientID: this.getTeamioPatientID(props),
            ClaimNum: props.row.ClaimNum ? props.row.ClaimNum : null,
            TaskTypeID: this.getTaskTypeID(),
            EmployeeID: this.userID,
            OldFollowupDate: moment(props.row.FollowupDate).format('YYYY-MM-DD'),
        }

        //console.log(`payload: ${JSON.stringify(payload)}`);

        try {
            //console.log(`now calling backend`);
            await this.removeInitialFollowupDate(payload);
            //console.log(`got back from backend`);
            let originalDataIndex = this.getOriginalDataIndex(props);
            //console.log(`original index: ${originalDataIndex} and data: ${JSON.stringify(this.data[originalDataIndex])}`);
            if(originalDataIndex !== -1) {
                Vue.set(this.data[originalDataIndex], "FollowupDate", null);
                Vue.set(this.data[originalDataIndex], "Status", "Not Called");
                Vue.set(this.data[originalDataIndex],"InitialFollowupDateSet",null);
                Vue.set(this.data[originalDataIndex],"CreatedAutomatically",null);
                Vue.set(this.data[originalDataIndex],"pastDue",false);
                if(this.initialFollowupNotes[i] !== '')
                    Vue.set(this.data[originalDataIndex], "InitialFollowupNote", null);
            }
        } catch (err) {
            console.error(err);
        } finally {
            Vue.set(this.followupActionPlanLoading, i, null);
            this.followupDateChangeInProgress[i] = false;
            this.$refs[`${props.index-1}-change-followup-date-dropdown`].hide(true);
        }

    },

    async _stopFollowups() {
        let props = this.followupParams;
        let i = Math.max(0, props.index - 1);
        this.followupDateChangeInProgress[i] = true;
        Vue.set(this.followupActionPlanLoading, i, true);

        let payload = {
            SiteID: this.siteID,
            TeamioPatientID: this.getTeamioPatientID(props),
            ClaimNum: props.row.ClaimNum ? props.row.ClaimNum : null,
            TaskTypeID: this.getTaskTypeID(),
            EmployeeID: this.userID,
        }

        //console.log(`payload: ${JSON.stringify(payload)}`);

        try {
            await this.stopFollowups(payload);
            let originalDataIndex = this.getOriginalDataIndex(props);
            //console.log(`original index: ${originalDataIndex} and data: ${JSON.stringify(this.data[originalDataIndex])}`);
            if(originalDataIndex !== -1) {
                Vue.set(this.data[originalDataIndex], "FollowupStopped", 1);
                Vue.set(this.data[originalDataIndex], "FollowupDate", null);
            }
        } catch (err) {
            console.error(err);
        } finally {
            Vue.set(this.followupActionPlanLoading, i, null);
            this.followupDateChangeInProgress[i] = false;
            this.$refs[`${props.index-1}-change-followup-date-dropdown`].hide(true);
        }
    },

    async _resetFollowups() {
        let props = this.followupParams;
        let i = Math.max(0, props.index - 1);
        this.followupDateChangeInProgress[i] = true;
        Vue.set(this.followupActionPlanLoading, i, true);

        let payload = {
            SiteID: this.siteID,
            TeamioPatientID: this.getTeamioPatientID(props),
            ClaimNum: props.row.ClaimNum ? props.row.ClaimNum : null,
            TaskTypeID: this.getTaskTypeID(),
            EmployeeID: this.userID,
        }

        //console.log(`payload: ${JSON.stringify(payload)}`);

        try {
            await this.resetFollowups(payload);
            let originalDataIndex = this.getOriginalDataIndex(props);
            //console.log(`original index: ${originalDataIndex} and data: ${JSON.stringify(this.data[originalDataIndex])}`);
            if(originalDataIndex !== -1) {
                //Vue.set(this.data[originalDataIndex], "FollowupReset", 1);
                Vue.set(this.data[originalDataIndex], "FollowupDate", null);
                Vue.set(this.data[originalDataIndex], "Status", 'Not Called');
                Vue.set(this.data[originalDataIndex], "Attempts", 0);
                Vue.set(this.data[originalDataIndex], "FollowupStopped", 0);
                Vue.set(this.data[originalDataIndex], "TeamioFollowupStatusID", null);
                Vue.set(this.data[originalDataIndex], "completed", 'false');
            }
        } catch (err) {
            console.error(err);
        } finally {
            Vue.set(this.followupActionPlanLoading, i, null);
            this.followupDateChangeInProgress[i] = false;
        }
    },

    getOriginalDataIndex(props) {
        let originalIndex;
        let pageIamOn;
        let uniqueID;

        switch(this.extra.detailPageName) {
            case 'AgingDetails'                 : pageIamOn = 'OUTSTANDING_CLAIMS'; uniqueID = 'ClaimNum'; break;
            case 'PatientBalancesDetails'       : pageIamOn = 'PATIENT_BALANCES'; uniqueID = 'PatNum'; break;
            case 'OutstandingPreAuthDetails'    : pageIamOn = 'OUTSTANDING_PREAUTH'; uniqueID = 'ClaimNum'; break;
            case 'ProcessedPreAuthDetails'      : pageIamOn = 'PROCESSED_PREAUTH'; uniqueID = 'ClaimNum'; break;
            case 'OverdueRecallDetails'         : pageIamOn = 'OVERDUE_RECALL'; uniqueID = 'PatNum'; break;
            case 'unscheduledPatientDetails'    : pageIamOn = 'UNSCHEDULED_TREATMENT'; uniqueID = 'PatNum'; break;
            case 'BrokenAppointmentDetails'     : pageIamOn = 'BROKEN_APPOINTMENT'; uniqueID = 'PatNum'; break;
        }

        /*
        switch(this.extra.detailPageName) {
            case 'AgingDetails'                 : pageIamOn = 'OUTSTANDING_CLAIMS'; uniqueID = 'ClaimNum'; break;
            case 'PatientBalancesDetails'       : pageIamOn = 'PATIENT_BALANCES'; uniqueID = 'TeamioPatientID'; break;
            case 'OutstandingPreAuthDetails'    : pageIamOn = 'OUTSTANDING_PREAUTH'; uniqueID = 'ClaimNum'; break;
            case 'ProcessedPreAuthDetails'      : pageIamOn = 'PROCESSED_PREAUTH'; uniqueID = 'ClaimNum'; break;
            case 'OverdueRecallDetails'         : pageIamOn = 'OVERDUE_RECALL'; uniqueID = 'TeamioPatientID'; break;
            case 'unscheduledPatientDetails'    : pageIamOn = 'UNSCHEDULED_TREATMENT'; uniqueID = 'TeamioPatientID'; break;
            case 'BrokenAppointmentDetails'     : pageIamOn = 'BROKEN_APPOINTMENT'; uniqueID = 'TeamioPatientID'; break;
        }
        */

        if(uniqueID === 'PatNum') {
            if(!props.row['PatNum'])
                props.row['PatNum'] = props.row.TeamioPatientID.substring(props.row.TeamioPatientID.lastIndexOf('-')+1);
        }
        //console.log(`uniqueID: ${uniqueID}, props.row: ${JSON.stringify(props.row)}, props.row[uniqueID] = ${props.row[uniqueID]}`);

        /* I could have used props.index -1 here as the originalIndex. However, what we found out is that if the user is using a filter to find someone
        that this would be the wrong value. So it's better to find the exact row first and get its index vs using the props.index */

        //return props.index-1;
        //console.log('printing this.data below');
        //console.dir(this.data);

        if(uniqueID === 'PatNum') {
            originalIndex = _.findIndex(this.data, function(o) { return o[uniqueID] === props.row[uniqueID]; });
            if(originalIndex === -1)
                originalIndex = _.findIndex(this.data, function(o) { return o['TeamioPatientID'] === props.row['TeamioPatientID']; });
        } else
            originalIndex = _.findIndex(this.data, function(o) { return o[uniqueID] === props.row[uniqueID]; });
        return originalIndex;

    },

    getParams(patNum, claimNum, teamioPatientID=null) {
        return {
            ...this.extra,
            patientID:  teamioPatientID === null ? `${this.siteID}-${patNum}` : teamioPatientID,
            claimID: claimNum
        }
    },

    getListPageName() {
        let ret;
        if (this.isOverdueRecallPage())
            ret = 'OverdueRecallList';
        else if(this.isUnscheduledTxPage())
            ret = 'unscheduledList';
        else
            ret = '';
        return ret;
    },

    getListParams(listID) {
        return {
            listID: listID
        }
    },

    getTaskTypeID() {
        let taskTypeID = null;
        switch(this.extra.detailPageName) {
            case 'AgingDetails'                 : taskTypeID = 2; break;
            case 'OverdueRecallDetails'         : taskTypeID = 3; break;
            case 'unscheduledPatientDetails'    : taskTypeID = 4; break;
            case 'OutstandingPreAuthDetails'    : taskTypeID = 5; break;
            case 'ProcessedPreAuthDetails'      : taskTypeID = 6; break;
            case 'PatientBalancesDetails'       : taskTypeID = 7; break;
            case 'BrokenAppointmentDetails'     : taskTypeID = 8; break;
            default: taskTypeID = null;
        }
        return taskTypeID;
    },

    isAdminPage() {
        return this.$route.path === '/app/admin' || this.$route.path === '/app/admin-by-employee';
    },

    isAdminPageByEmployee() {
        return this.$route.path === '/app/admin-by-employee';
    },

    isOverdueRecallPage() {
        return this.$route.path === '/app/overdue-recall';
    },

    /*
        "Auto Reminders Sent = 0" answers two completely different questions with the same
        character: "the reminder hasn't gone out YET" and "the reminder never went out". On
        2026-08-26 a practice read a mid-morning 0 as a failure and it was simply not 9 AM's send
        yet — the reminder went out that afternoon. These two helpers make the cell say which.

        Deliberately conservative: it reports SCHEDULING, never "delivered". The row has no send
        ledger, so claiming anything stronger would be a guess dressed up as a fact.
    */
    recallReminderSendHour() {
        const raw = this.recallReminderSetting('recallReminderTimeOfDayToSend');
        const n = parseInt(raw, 10);
        // Mirrors the backend's coerceSendHour: out-of-range / non-numeric falls back to 8.
        return (Number.isInteger(n) && n >= 8 && n <= 20) ? n : 8;
    },

    recallReminderSendHourLabel() {
        const h = this.recallReminderSendHour();
        return h === 12 ? '12 PM' : (h > 12 ? `${h - 12} PM` : `${h} AM`);
    },

    // Read one key out of the APPT_REMINDER_SETTINGS JSON blob without assuming it parses.
    recallReminderSetting(key) {
        try {
            const raw = this.allSettings
                && this.allSettings.APPT_REMINDER_SETTINGS
                && this.allSettings.APPT_REMINDER_SETTINGS.PropertyValue;
            if (!raw) return undefined;
            return JSON.parse(raw)[key];
        } catch (e) {
            return undefined;
        }
    },

    // "Pending" = the due date hasn't passed, so a 0 is expected rather than concerning.
    recallReminderPending(row) {
        if (!row || !row.DateDue) return false;
        const due = moment(row.DateDue).startOf('day');
        if (!due.isValid()) return false;
        return due.isSameOrAfter(moment().startOf('day'));
    },

    /*
        The office's on/off intent — NOT the raw recallReminders key.

        MIRRORS appt-reminder-service lib/recall-attempt1.js resolveRecallEnabled(). Once a site's
        exe leg is retired, recallReminders becomes the EXE'S MUTE and is 'FALSE' on a perfectly
        healthy cloud-sending practice; reading it directly would tell that office "Auto Recall
        Reminders are turned off" while their patients were being texted. recallRemindersEnabled
        carries the intent from then on.

        ABSENT falls back to recallReminders, so every site that predates the key reads as it always
        did. An un-migrated site deliberately ignores the new key — the exe can only ever read
        recallReminders, and honouring a key it cannot see would let the UI and the exe disagree.
    */
    recallRemindersOn() {
        const leg = String(this.recallReminderSetting('recallSendLeg') || 'EXE').trim().toUpperCase();
        const legacy = String(this.recallReminderSetting('recallReminders')).toUpperCase() === 'TRUE';
        if (leg !== 'BACKEND') return legacy;
        const raw = this.recallReminderSetting('recallRemindersEnabled');
        if (raw === undefined || raw === null || String(raw).trim() === '') return legacy;
        return String(raw).trim().toUpperCase() === 'TRUE';
    },

    recallReminderTooltip(row) {
        const enabled = this.recallRemindersOn();
        if (!enabled) {
            return 'No automated reminder — Auto Recall Reminders are turned off for this practice. '
                + 'Turn them on under Appointment Reminder settings.';
        }
        if (!row || !row.DateDue) return 'No automated recall reminder has been sent to this patient.';

        const due = moment(row.DateDue).startOf('day');
        const today = moment().startOf('day');
        const at = this.recallReminderSendHourLabel();

        if (due.isAfter(today)) {
            return `Not due yet — the first automated reminder is scheduled for ${due.format('MM/DD/YYYY')} at ${at}.`;
        }
        if (due.isSame(today)) {
            return moment().hour() < this.recallReminderSendHour()
                ? `Due today — the automated reminder is scheduled to go out at ${at}.`
                : `Due today — the automated reminder goes out at ${at} and may still be processing.`;
        }
        return 'No automated reminder was sent for this due date. Most often the patient has no mobile '
            + 'number, is marked "no text messages" in Open Dental, or has opted out. Reach out manually if needed.';
    },

    isAgingPage(){
        return this.$route.path === '/app/aging';
    },

    isOverdueRecallListPage() {
        return this.$route.path.includes('/app/overdue-recall-list');
    },

    isUnscheduledTxPage() {
        return this.$route.path === '/app/unscheduled-treatment';
    },

    isUnscheduledTxListPage() {
        return this.$route.path.includes('/app/unscheduled-list');
    },

    isBrokenAppointmentsPage() {
        return this.$route.path === '/app/broken-appointments';
    },

    _removePatient(task) {
        this.$emit('remove-patient-from-list', task);
    },

    /*
        BelongsToListID is in different variables based on which page you set the initial followup date ON. Master page vs list page
    */
    getBelongsToListID(props) {
        //console.log(`this.extra: ${JSON.stringify(this.extra)}, listid: ${this.extra.listID}, props-row: ${JSON.stringify(props.row)}`);
        let retVal;
        if(this.isUnscheduledTxPage() || this.isOverdueRecallPage()) {
            retVal = props.row.BelongsToListID;
        }
        else {
            retVal = this.extra.listID;
        }
        //console.log(`retVal: ${retVal}`);
        return retVal;
    },

    getTeamioPatientID(props) {
        //console.log(`${JSON.stringify(props)}`);
        return props.row.PatNum ? `${this.siteID}-${props.row.PatNum}` : props.row.TeamioPatientID;
    },

    getUniqueID(props) {
        let primaryId = null;

        // Determine the primary identifier based on page type and data
        if(this.isAdminPage() || this.isAdminPageByEmployee()) {
            if(this.getTaskTypeID() === 3 || this.getTaskTypeID() === 4 || this.getTaskTypeID() === 8 || this.getTaskTypeID() === 7) {
                primaryId = props.row.PatNum ? `${this.siteID}-${props.row.PatNum}` : props.row.TeamioPatientID;
            } else {
                primaryId = props.row.ClaimNum;
            }
        } else {
            if(this.isOverdueRecallPage() || this.isOverdueRecallListPage() || this.isUnscheduledTxPage() || this.isUnscheduledTxListPage() || this.isBrokenAppointmentsPage() || this.getTaskTypeID() === 7) {
                primaryId = props.row.PatNum ? `${this.siteID}-${props.row.PatNum}` : props.row.TeamioPatientID;
            } else {
                primaryId = props.row.ClaimNum;
            }
        }

        // If primaryId is null or undefined, use the original index as a fallback
        if (primaryId === null || typeof primaryId === 'undefined') {
            const originalIndex = this.getOriginalDataIndex(props); // Find index in the original data array
            // Return a fallback ID based on the index. Ensure it's a string and won't clash with real IDs.
            // Use a positive index to avoid invalid HTML IDs (IDs cannot start with numbers or contain hyphens in certain positions)
            // If originalIndex is -1, use a timestamp-based fallback to ensure uniqueness
            if (originalIndex === -1) {
                return `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            }
            return `fallback_${Math.abs(originalIndex)}`;
        }

        // Return the primary identifier (ensuring it's a string if it's a number like ClaimNum/PatNum)
        return String(primaryId);
    },

    async createFollowup(event, props) {
        event.preventDefault();
        var _this = this;

        // Use props.index - 1 as the index for this table instance's loading status
        const instanceIndex = Math.max(0, (props.index || 1) - 1);

        /* Removed logic using originalDataIndex for loadingStatus
        let originalDataIndex = this.getOriginalDataIndex(props);
        Vue.set(this.loadingStatus, originalDataIndex, true);
        */
        Vue.set(this.loadingStatus, instanceIndex, true); // Use instanceIndex (already calculated with safety check)
        Vue.set(this.loadingErrorStatus, instanceIndex, false); // Reset error status

        let patNum = props.row.PatNum;
        this.teamioPatientID = props.row.TeamioPatientID;
        let claimNum = props.row.ClaimNum;

        this.teamioPatientID = patNum ? `${this.siteID}-${patNum}` : this.teamioPatientID;
        this.lastFollowedUpWithTeamioPatientID = this.teamioPatientID;

        let query;

        /*
        let payload = {
            siteID: this.siteID,
            teamioPatientID: this.teamioPatientID,
            employeeID: this.userID,
        }*/

        let attemptTrackingPayload = {
                cacheName: process.env.VUE_APP_MOMENTO_CACHE,
                cacheSet: 'fuattempts',
                element: this.teamioPatientID
        }

        try {
            // PHASE 2 PREVENTION: Enhanced error handling with guaranteed cleanup
            await this.startTrackingFUAttempt(attemptTrackingPayload);
            this.attemptTracking = true;
            
            // PHASE 2 PREVENTION: Track the active session for cleanup
            this.$store.commit('cache/addActiveFollowupSession', { 
                patientId: this.teamioPatientID, 
                payload: attemptTrackingPayload 
            });

            switch(this.extra.detailPageName) {
                case 'OverdueRecallDetails' :       query = {
                                                        type: 'OVERDUE_RECALL_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;

                case 'unscheduledPatientDetails':   query = {
                                                        type: 'UNSCHEDULED_TREATMENT_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'BrokenAppointmentDetails':    query = {
                                                        type: 'BROKEN_APPOINTMENT_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        adjustments: localStorage.getItem('brokenApptAdjustments') || '',
                                                        codes: localStorage.getItem('brokenApptCodes') || '',
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'AgingDetails':                query = {
                                                        type: 'AGING_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        claimID: claimNum,
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'OutstandingPreAuthDetails':   query = {
                                                        type: 'OUTSTANDING_PREAUTH_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        claimID: claimNum,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'ProcessedPreAuthDetails':     query = {
                                                        type: 'PROCESSED_PREAUTH_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                case 'PatientBalancesDetails':      query = {
                                                        type: 'PATIENT_BALANCES_DETAILS',
                                                        siteID: this.siteID,
                                                        patientID: this.teamioPatientID,
                                                        _date: moment().format('YYYY-MM-DD'),
                                                        country: localStorage.getItem('country') || 'USA'
                                                    };
                                                    break;
                default: break;

            }

            const followupHandle = this.$showPanel({
                component : CreateFollowup,
                openOn: 'right',
                width: 1020,
                disableBgClick: true,
                disableEscClick: true,
                cssClass: 'followUpPanelClass',
                props: {
                    teamioPatientID : this.teamioPatientID,
                    claimNum: claimNum,
                    query: query,
                    detailPageName: this.extra.detailPageName
                }
            });

            // PHASE 2 PREVENTION: Enhanced promise handling with guaranteed cleanup
            followupHandle.promise
                .then(async(result) => {
                // Update data using originalDataIndex if needed for reactivity with source
                let originalDataIndex = this.getOriginalDataIndex(props);

                if(result.Status && result.Attempts) {
                    // Use originalDataIndex for updating the underlying data prop
                    if (originalDataIndex !== -1) {
                        Vue.set(this.data[originalDataIndex], "Status", result.Status);
                        Vue.set(this.data[originalDataIndex],"TeamioFollowupStatusID", result.TeamioFollowupStatusID);
                        Vue.set(this.data[originalDataIndex], "FollowupDate", result.FollowupDate);
                        Vue.set(this.data[originalDataIndex], "Attempts", result.Attempts);
                        Vue.set(this.data[originalDataIndex], "AttemptDetails", result.AttemptDetails);
                        Vue.set(this.data[originalDataIndex], "pastDue", 'false');
                        Vue.set(this.data[originalDataIndex], "completed", 'true');
                        Vue.set(this.data[originalDataIndex], "Overridden", result.Overridden);
                        Vue.set(this.data[originalDataIndex], result.StatusUpdatedAtPropertyName, result.StatusUpdatedAtPropertyNameValue);
                    }

                    this.$emit('call-goals-updated', originalDataIndex);
                }
                
                if(originalDataIndex !== -1) {
                    Vue.set(this.data[originalDataIndex],"NextVisit",result.nextVisit);
                }

                // Use instanceIndex for loading status (recalculate to ensure it's in scope)
                const currentInstanceIndex = Math.max(0, (props.index || 1) - 1);
                Vue.set(this.loadingStatus, currentInstanceIndex, false);

                // PHASE 2 PREVENTION: Guaranteed cleanup with error handling
                if(_this.attemptTracking) {
                    Vue.set(_this, 'lastFollowedUpWithTeamioPatientID', null);
                    Vue.set(_this, 'attemptTracking', false);

                    try {
                        await _this.stopTrackingFUAttempt(attemptTrackingPayload);
                    } catch (cleanupError) {
                        console.error('[FOLLOWUP] Error during cleanup:', cleanupError);
                        // Still remove from local tracking even if cache operation fails
                    } finally {
                        // PHASE 2 PREVENTION: Always remove from local session tracking
                        _this.$store.commit('cache/removeActiveFollowupSession', _this.teamioPatientID);
                    }
                }
            })
            .catch(async(error) => {
                // PHASE 2 PREVENTION: Handle promise rejection with cleanup
                console.error('[FOLLOWUP] Follow-up panel promise rejected:', error);

                // Recalculate instanceIndex to ensure it's in scope
                const currentInstanceIndex = Math.max(0, (props.index || 1) - 1);
                Vue.set(this.loadingStatus, currentInstanceIndex, false);

                if(this.attemptTracking) {
                    Vue.set(this, 'lastFollowedUpWithTeamioPatientID', null);
                    Vue.set(this, 'attemptTracking', false);

                    try {
                        await this.stopTrackingFUAttempt(attemptTrackingPayload);
                    } catch (cleanupError) {
                        console.error('[FOLLOWUP] Cleanup after rejection failed:', cleanupError);
                    } finally {
                        // PHASE 2 PREVENTION: Always remove from local session tracking
                        this.$store.commit('cache/removeActiveFollowupSession', this.teamioPatientID);
                    }
                }
            });

        } catch(err) {
            // PHASE 2 PREVENTION: Enhanced error handling with cleanup
            // Only log unexpected errors, not the expected "exists" case
            if (err.message !== 'exists') {
                console.error('[FOLLOWUP] Error starting follow-up tracking:', err);
            }

            // PHASE 2 PREVENTION: Cleanup on any error
            if(this.attemptTracking) {
                Vue.set(this, 'attemptTracking', false);
                this.$store.commit('cache/removeActiveFollowupSession', this.teamioPatientID);
            }
            
            // Recalculate instanceIndex to ensure it's in scope
            const currentInstanceIndex = Math.max(0, (props.index || 1) - 1);
            Vue.set(this.loadingStatus, currentInstanceIndex, false);

            if(err.message === "exists") {
                // Show actionable toast for authorized users to force-release the lock
                if (this.$can && this.$can('update', 'followupDate')) {
                    this.$toast.error({
                        component: ToastComponent,
                        props: {
                            payload: attemptTrackingPayload
                        }
                    }, {
                        timeout: 10000
                    });
                } else {
                    this.$toast.error(`There's already someone following-up with this patient`);
                }
                // https://www.npmjs.com/package/vue-toastification/v/0.2.1

                /*
                const myJSX = (
                    <div>
                        <h1>My Title</h1>
                        <span>My text</span>
                        <button>Clear</button>
                    </div>
                );

                let _this = this;

                const content = {
                    // Your component or JSX template
                    //component: MyComponent,
                    component: ToastComponent,

                    // Props are just regular props, but these won't be reactive
                    props: {
                        payload: attemptTrackingPayload
                    },

                    // Listeners will listen to and execute on event emission
                    listeners: {
                        click: () => console.log("Clicked!"),
                        clearFollowupCache: async function(attemptTrackingPayload) {
                            console.log('got', attemptTrackingPayload);
                            await _this.stopTrackingFUAttempt(attemptTrackingPayload);
                            console.log("attempt stopped");
                        }

                        //myEvent: myEventHandler
                    }
                };
                this.$toast.error(content);
                */

                /*
                this.$toast.error(ToastComponent,
                {
                    props: {
                        payload: attemptTrackingPayload
                    }
                });
                */

                // FIXED: Define originalDataIndex in the correct scope
                let originalDataIndex = this.getOriginalDataIndex(props);
                Vue.set(this.data[originalDataIndex], 'teamioActive', false);
                // Recalculate instanceIndex to ensure it's in scope
                const currentInstanceIndex = Math.max(0, (props.index || 1) - 1);
                Vue.set(this.loadingStatus, currentInstanceIndex, false);
                Vue.set(this.loadingErrorStatus, currentInstanceIndex, true);
                window.scrollTo(0, 0);
                return false;
            } else {
                // Recalculate instanceIndex to ensure it's in scope
                const currentInstanceIndex = Math.max(0, (props.index || 1) - 1);
                Vue.set(this.loadingErrorStatus, currentInstanceIndex, true);
                Vue.set(this.loadingStatus, currentInstanceIndex, false);
            }
        }
    },

    attemptDetails(ad) {
        if(ad) {
            return ad.split(',');
        } else
            return [];
    },

    clearSelection() {
      // Emit event to parent to clear selection
      this.$emit('clear-selection');
    }
  },

  computed: {
        hasSelectionColumn() {
            return Array.isArray(this.columns) && this.columns.length > 0 && this.columns[0] === 'id';
        },

    // Only promise "It will save to commlog..." when the site has opted in
    // (GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG); off by default.
    followupNotePlaceholder() {
      return this.allSettings && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG && this.allSettings.GENERAL_FOLLOWUP_NOTE_COPY_TO_COMMLOG.PropertyValue === 'TRUE'
        ? 'Optional. Add notes here. It will save to commlog...'
        : 'Optional. Add notes here...';
    },

    // Safe accessor for currency symbol with fallback
    currencySymbol() {
      return this.allSettings && 
             this.allSettings.GENERAL_CURRENCY_SYMBOL && 
             this.allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue 
             ? this.allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue 
             : '$';
    },

    tableOptions() {
      // Map column keys to human-readable names for filter placeholders
      const columnLabels = {
        Patient: 'Patient',
        Guarantor: 'Guarantor',
        Carrier: 'Carrier',
        Status: 'Status',
        Attempts: 'Attempts',
        AdjustmentProcedureCode: 'Procedure Code',
        fee: 'Fee',
        claimFee: 'Claim Fee',
        insurance: 'Insurance',
        Provider: 'Provider',
        AutoRemindersSent: 'Auto Reminders',
        LastHygienist: 'Last Hygienist',
        dateBroken: 'Date',
        DateDue: 'Due Date',
        dateTxPlanned: 'Date Tx Planned',
        ...(this.options.headings || {})
      };

      return {
        ...this.options,
        rowClassCallback: this.customRowClassCallback,
        filterPlaceholder: (column) => {
          const label = columnLabels[column] || column;
          return `Filter by ${label}`;
        }
      };
    },

    getAssignedToEmployeeID() {
        if(this.allSettings) {
            let assignedToEmployeeID = null;
            switch(this.extra.detailPageName) {
                case 'AgingDetails'                 : assignedToEmployeeID = this.allSettings.OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'OverdueRecallDetails'         : assignedToEmployeeID = this.allSettings.OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'unscheduledPatientDetails'    : assignedToEmployeeID = this.allSettings.UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'OutstandingPreAuthDetails'    : assignedToEmployeeID = this.allSettings.OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'ProcessedPreAuthDetails'      : assignedToEmployeeID = this.allSettings.PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'PatientBalancesDetails'       : assignedToEmployeeID = this.allSettings.PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'BrokenAppointmentDetails'     : assignedToEmployeeID = this.allSettings.BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                default: assignedToEmployeeID = null;
            }
            return assignedToEmployeeID;
        }
    },

    /*
    get employees from settings for the corresponding task (Aging: get all employees assigned to under main office settings in Outstanding Claims)
    and return an array of employees
    */
    filteredEmployees(){
        if(this.allSettings) {
            let employeesList = null;
            let employeesArray = [];
            let filteredArray = [];

            //employeesList can be "[employeeID1]" or "[employeeID1]|[employeeID2]|..."

            switch(this.extra.detailPageName) {
                case 'AgingDetails'                 : employeesList = this.allSettings.OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'OverdueRecallDetails'         : employeesList = this.allSettings.OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'unscheduledPatientDetails'    : employeesList = this.allSettings.UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'OutstandingPreAuthDetails'    : employeesList = this.allSettings.OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'ProcessedPreAuthDetails'      : employeesList = this.allSettings.PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'PatientBalancesDetails'       : employeesList = this.allSettings.PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                case 'BrokenAppointmentDetails'     : employeesList = this.allSettings.BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID.PropertyValue; break;
                default: employeesList = null;
            }

            //now we need to split the string into array separated by the "|" character

            //check if at least one employee
            if (employeesList){
                employeesArray = employeesList.split("|");
                if (employeesArray){

                    /*
                    since this list only has strings of employeeIDs, we need more info like their name, site id, etc.
                    we have access to mixin's activeEmployees so we can filter that array by the ones we find in employeesArray
                    */

                    filteredArray = _.filter(this.activeEmployees,employee =>
                        employeesArray.includes(employee.EmployeeID)
                    );

                }

            }

            return filteredArray;
        }
    },

    isAptReminderPaidFor() {
      if(this.allSettings && this.allSettings.APPT_REMINDER_PAID_FOR) {
        if (this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === 'TRUE')
          return true;
        else
          return false;
      } else
        return false;
    },
  }
}
</script>

<style lang="scss" scoped>
.tooltip-inner a:hover {
    text-decoration: none;
    color: #ffffff;
}

.VuePagination__pagination-item .active {
    color: green !important;
}

/* Admin table header styles */
.admin-table-header {
    margin-bottom: 12px;

    .title-section {
        margin-bottom: 10px;
        padding-left: 0; /* Align with table edge */
        
        .table-title {
            font-size: 1.25rem;
            font-weight: 600;
            color: #28a745;
            margin: 0;
        }
    }

    .unified-action-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background-color: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 6px;
        margin-bottom: 10px;
        margin-left: 0;  /* Align with table edge */

        .selection-status {
            display: flex;
            align-items: center;
            font-size: 14px;
            
            .selected-count {
                color: #155724;
                
                .fa-check-circle {
                    color: #28a745;
                }
            }
            
            .no-selection {
                color: #6c757d;
                
                .fa-square-o {
                    color: #adb5bd;
                }
            }
        }

        .action-buttons {
            display: flex;
            align-items: center;
            gap: 8px;

            .action-btn {
                font-size: 12px;
                padding: 5px 10px;
                border-radius: 4px;
                font-weight: 500;
                white-space: nowrap;
                
                .fa {
                    font-size: 11px;
                }
                
                &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                &.btn-outline-primary {
                    color: #007bff;
                    border-color: #007bff;
                    
                    &:hover:not(:disabled) {
                        background-color: #007bff;
                        color: white;
                    }
                }

                &.btn-outline-secondary {
                    color: #6c757d;
                    border-color: #6c757d;
                    
                    &:hover:not(:disabled) {
                        background-color: #6c757d;
                        color: white;
                    }
                }
            }
        }
    }

    /* Responsive design */
    @media (max-width: 992px) {
        .unified-action-bar {
            flex-direction: column;
            gap: 12px;
            text-align: center;

            .selection-status {
                justify-content: center;
            }

            .action-buttons {
                justify-content: center;
                flex-wrap: wrap;
                gap: 6px;
            }
        }
    }

    @media (max-width: 768px) {
        .unified-action-bar {
            .action-buttons {
                .action-btn {
                    font-size: 11px;
                    padding: 4px 8px;
                    
                    .fa {
                        font-size: 10px;
                    }
                }
            }
        }
    }
}

.ai-call-btn {
    padding: 0 4px;
    font-size: 14px;
    line-height: 1;
    vertical-align: middle;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 3px;
    cursor: pointer;
    opacity: 0.7;
    transition: opacity 0.15s;

    &:hover:not(:disabled) {
        opacity: 1;
        border-color: #ccc;
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }

    &.ai-call-loading {
        opacity: 0.5;
    }
}

</style>

<!-- Non-scoped styles to fix checkbox column visibility in responsive tables -->
<style lang="scss">
/* FIX: Checkbox column - use proper spacing instead of overflow:visible */
.admin-table-wrapper .table-responsive.overdue-recall-table {
    margin-left: 0 !important;
    margin-top: 0 !important;
}

.admin-table-wrapper .overdue-recall-table table {
    width: 100% !important;
    table-layout: auto !important;
}

/* Checkbox column specific fixes (ONLY when selection checkbox column exists) */
.admin-table-wrapper .overdue-recall-table.has-selection-col td:first-child,
.admin-table-wrapper .overdue-recall-table.has-selection-col th:first-child {
    min-width: 40px !important;
    width: 40px !important;
    padding-left: 10px !important;
    padding-right: 4px !important;
}

.admin-table-wrapper .overdue-recall-table.has-selection-col .abc-checkbox {
    margin: 0;
    padding: 0;
}

/* ============================================
   Sorting Arrows & Header Alignment - Global Fix
   ============================================ */
.admin-table-wrapper .overdue-recall-table {
    /* Column width fixes - ensure enough space for header + sort icon */
    /* VueTables adds th id="VueTables_th--columnName" (double-dash) */
    th[id^="VueTables_th--DateDue"],
    th[id^="VueTables_th--dateBroken"],
    th[id^="VueTables_th--DateSent"],
    th[id^="VueTables_th--dateOfService"],
    th[id^="VueTables_th--dateTxPlanned"],
    th[id^="VueTables_th--lastVisit"] {
        min-width: 95px !important;
        width: 95px !important;
    }

    /* Patient column - ensure adequate width */
    th[id^="VueTables_th--Patient"] {
        min-width: 200px !important;
    }
    
    /* Table header cells */
    thead th {
        vertical-align: middle !important;
        padding: 8px 6px !important;
        font-size: 12px;
        font-weight: 600;
        background-color: #f8f9fa;
        border-bottom: 2px solid #dee2e6;
    }

    /* Sortable columns - ONLY on heading row (filters row should stay block) */
    thead tr.VueTables__heading th.VueTables__sortable {
        cursor: pointer;
        user-select: none;
        white-space: nowrap !important;

        &:hover {
            background-color: #e9ecef;
        }

        /* Keep heading text and sort icon inline */
        > * {
            display: inline !important;
            vertical-align: middle !important;
        }
    }

    /* Header row: keep header text and sort icon inline (do NOT apply to filters row) */
    thead tr.VueTables__heading th > div,
    thead tr.VueTables__heading th > span {
        display: inline !important;
        vertical-align: middle !important;
    }

    /* Filters row: ensure filter controls have room and aren't clipped */
    thead tr.VueTables__filters-row th {
        vertical-align: bottom !important;
        padding: 6px 6px !important;
    }

    thead tr.VueTables__filters-row th > div {
        display: block !important;
    }

    /* Defensive: if VueTables adds sortable class to filters cells, force block layout */
    thead tr.VueTables__filters-row th.VueTables__sortable > * {
        display: block !important;
    }

    thead tr.VueTables__filters-row input,
    thead tr.VueTables__filters-row select {
        box-sizing: border-box;
        width: 100%;
        min-height: 30px;
    }
    
    /* Sort icons - CRITICAL FIX: must be inline with text */
    .VueTables__sort-icon {
        display: inline !important;
        font-size: 9px !important;
        vertical-align: middle !important;
        margin-left: 4px !important;
        opacity: 0.4;
        
        /* Active sort direction */
        &.glyphicon-chevron-up,
        &.glyphicon-chevron-down {
            opacity: 1;
            color: #1976d2;
        }
    }
    
    /* CRITICAL: Header slot divs must be inline so sort icon stays on same line */
    thead tr.VueTables__heading th.VueTables__sortable > div {
        display: inline !important;
        vertical-align: middle !important;
    }
    
    /* Sortable header spans */
    thead tr.VueTables__heading th.VueTables__sortable > span {
        display: inline !important;
        vertical-align: middle !important;
    }
    
    /* Multi-line headers (like Ins Amts, Tx Plan Amt) */
    .header-multiline {
        display: inline-block !important;
        vertical-align: middle !important;
        line-height: 1.2;
        
        small {
            display: block;
            font-size: 10px;
            color: #666;
        }
    }
    
    /* Table body cells */
    tbody td {
        padding: 6px 8px !important;
        vertical-align: middle;
        font-size: 12px;
    }
    
    /* Alternate row coloring for readability */
    tbody tr:nth-child(even) {
        background-color: #fafbfc;
    }
    
    tbody tr:hover {
        background-color: #f0f7ff;
    }
    
    /* Attempts column - limit icons and show +N indicator */
    .attempts-icons {
        display: inline-flex;
        align-items: center;
        flex-wrap: nowrap;
        
        i {
            font-size: 14px;
        }
    }
    
    .attempts-more-indicator {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: #28a745;
        color: white;
        font-size: 10px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 10px;
        margin-left: 3px;
        min-width: 22px;
    }
}

/* ============================================
   Follow Up Action Plan Column - Inline Icons Fix
   ============================================ */
.admin-table-wrapper .overdue-recall-table {
    /* Remove any left margin - let parent container control spacing */
    margin-left: 0 !important;
    margin-top: 0 !important;
    
    /* FollowUpActionPlan column - keep dropdown and icons on same line */
    td div[slot="FollowUpActionPlan"],
    td > div:has(.change-fu-date-dropdown),
    td > div:has(.not-set-dropdown) {
        display: flex !important;
        flex-wrap: nowrap !important;
        align-items: center !important;
        gap: 4px;
    }
    
    /* ONLY fix "Not Set" dropdown width to match date dropdowns */
    .not-set-dropdown {
        display: inline-flex !important;
        flex-shrink: 0;
        
        .btn {
            min-width: 110px !important;  /* Match width of date dropdowns */
        }
    }
    
    /* Date dropdowns - leave as-is, just ensure inline */
    .change-fu-date-dropdown {
        display: inline-flex !important;
        flex-shrink: 0;
    }
    
    /* Icons wrapper - keeps all inline icons together */
    .followup-icons-inline {
        display: inline-flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        vertical-align: middle !important;
        
        /* Don't use display !important on icons - it overrides v-show */
        i {
            vertical-align: middle;
            margin-left: 4px;
            flex-shrink: 0;
        }
    }
    
    /* Fix the cell containing FollowUpActionPlan */
    td:has(.change-fu-date-dropdown),
    td:has(.not-set-dropdown),
    td:has(.followup-icons-inline) {
        white-space: nowrap !important;
        
        > div:first-child {
            display: inline-flex !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
        }
    }
    
    /* Icon alignment - no display override to respect v-show */
    .fa-circle.text-danger,
    .fa-exclamation.text-danger,
    .la-refresh,
    .glyphicon-list-alt {
        vertical-align: middle;
    }
    
    /* Follow Up Action Plan cell - inline layout */
    .followup-action-cell {
        display: inline-flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        gap: 2px;

        > * {
            flex-shrink: 0;
        }
    }

    /* Hide duplicate default filter when custom filter slot is present */
    /* vue-tables-2 wraps both default filter + custom slot in a div when slot exists */
    .VueTables__AdjustmentProcedureCode-filter-wrapper > div > input:first-child,
    .VueTables__fee-filter-wrapper > div > input:first-child,
    .VueTables__insurance-filter-wrapper > div > input:first-child,
    .VueTables__lastVisit-filter-wrapper > div > input:first-child {
        display: none !important;
    }
}
</style>

<style type="scss" scoped>
caption .btn-toolbar {
    width: 100%; /* Make the caption take up the entire width */
    display: block; /* Ensure the caption is treated as a block element */
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
    color: #798892;
    text-align: right; /* Align text to the right */
    caption-side: bottom; }
</style>